import { type IdeaLabRunReport, formatSignedPercent, formatPlainPercent, formatDollars } from '../ideaLabUtils';
import { marketGroupLabel, type ReportView } from '../reportView';

export default function MarketGroupComparison({
    report,
    view
}: {
    report: Pick<IdeaLabRunReport, 'cells' | 'variants'>;
    view: ReportView;
}) {
    const cells = report.cells.filter(
        (cell) => cell.variantId === view.variantId && cell.windowRole === view.windowRole && cell.horizonSeconds === view.horizonSeconds
    );
    if (cells.length < 2) return null;
    const variant = report.variants.find((candidate) => candidate.variantId === view.variantId);
    return (
        <div className="space-y-2">
            <h4 className="text-sm font-semibold">Market comparison · {variant?.label}</h4>
            <div className="overflow-x-auto rounded border border-zinc-800">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-zinc-400">
                            {['Group', 'Priced', 'Missing', 'Immature', 'Symbols', 'Win rate', 'Mean', 'Median', 'Net P&L / contract'].map(
                                (label) => (
                                    <th className="p-2" key={label}>
                                        {label}
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {cells.map((cell) => (
                            <tr key={`${cell.entityType}:${cell.lens}`} className="border-t border-zinc-800">
                                <td className="p-2">{marketGroupLabel(cell.entityType, cell.lens)}</td>
                                <td className="p-2">{cell.supportCount}</td>
                                <td className="p-2">{cell.missingCount}</td>
                                <td className="p-2">{cell.immatureCount}</td>
                                <td className="p-2">{cell.distinctEntityCount}</td>
                                <td className="p-2">
                                    {formatPlainPercent(cell.supportCount > 0 ? (cell.winCount / cell.supportCount) * 100 : null)}
                                </td>
                                <td className="p-2">{formatSignedPercent(cell.meanReturnPercent)}</td>
                                <td className="p-2">{formatSignedPercent(cell.medianReturnPercent)}</td>
                                <td className="p-2">{cell.lens === 'option' ? formatDollars(cell.option.meanNetProfit) : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-zinc-400">
                Underlying returns measure price movement. Option returns use net P&amp;L divided by opening premium, with fees and contract
                economics shown separately. These fixed-horizon results do not rank portfolio performance across markets. Crypto options are
                not applicable.
            </p>
        </div>
    );
}
